<script setup>
import { ref, computed, watch } from 'vue'
import BcsDynamicText from '../BcsDynamicText.vue'
import { 
  formatDateGoogleReminders,
  daysDifferenceGoogleReminder
} from './dateUtils'

const props = defineProps({
  googleReminders: { type: Array, required: true },
  showGoogleReminders: { type: Boolean, default: false },
  sortDirection: { type: String, default: 'asc' },
  todayDate: { type: String, required: true },
  actualEmployer: { type: Object, required: true },
  isActionCenterMode: { type: Boolean, default: false },
  modalId: { type: String, default: 'reminders-sent' },
  subtitleId: { type: String, default: 'titleModalRemindersSent' }
})

defineEmits(['toggleSort'])

const showGoogleReminderInformation = ref({
  show: false,
  event: ''
})

// Track expanded rows in Action Center mode (by requirementKey)
const expandedRows = ref(new Set())

function toggleRowExpand(key) {
  const newSet = new Set(expandedRows.value)
  if (newSet.has(key)) {
    newSet.delete(key)
  } else {
    newSet.add(key)
  }
  expandedRows.value = newSet
}

function isRowExpanded(key) {
  return expandedRows.value.has(key)
}

// Reset expanded rows when modal data changes
watch(() => props.googleReminders, () => {
  expandedRows.value = new Set()
}, { deep: true })

const sortedEvents = computed(() => {
  return [...props.googleReminders].sort((a, b) => {
    const comparison = a.due_date.localeCompare(b.due_date)
    return props.sortDirection === 'asc' ? comparison : -comparison
  })
})

const handleShowReminderInfo = (event) => {
  showGoogleReminderInformation.value = {
    show: true,
    event: event
  }
}

const clearInfo = () => {
  showGoogleReminderInformation.value = {
    show: false,
    event: ''
  }
  expandedRows.value = new Set()
}

defineExpose({
  clearInfo
})
</script>

<template>
  <div
    class="modal fade"
    :id="modalId"
    tabindex="-1"
    :aria-labelledby="modalId"
    aria-hidden="true"
    :style="isActionCenterMode ? '--bs-modal-width: 1000px' : '--bs-modal-width: 853px'">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header px-4 d-flex flex-column bg-gray-200">
          <div class="d-flex w-100 justify-content-between">
            <span class="modal-title header-22" id="reminders-sent-label">
              <div>
                <i class="bi bi-calendar-check"></i>
                <strong>
                  {{
                    showGoogleReminderInformation.show ?
                      showGoogleReminderInformation.event.name
                    : 'Due Date Reminders Sent'
                  }}
                </strong>
              </div>
            </span>

            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"></button>
          </div>

          <p class="me-auto mb-0 text-16 text-gray-700 mt-1">
            <BcsDynamicText :text="subtitleId" />
          </p>
        </div>
        <div class="modal-body">
          <!-- Main table view -->
          <div
            v-if="!showGoogleReminderInformation.show"
            class="card table-container-events">
            <table class="table table-reminders">
              <thead class="table-reminders-sent thead-custom-red">
                <tr>
                  <th style="padding-left: 20px" scope="col" class="second-column">Requirement</th>
                  <!-- Employers column only in Action Center mode -->
                  <th v-if="isActionCenterMode" scope="col" class="employers-column">Employers</th>
                  <th scope="col" class="third-column">
                    Due
                    <i
                      @click="$emit('toggleSort')"
                      :class="{
                        'bi-sort-down': sortDirection === 'asc',
                        'bi-sort-up': sortDirection === 'desc'
                      }"
                      class="bi sort-icon"></i>
                  </th>
                  <th scope="col" class="">Reminder Scheduled For</th>
                  <th scope="col" class="">Reminder Sent</th>
                </tr>
              </thead>

              <tbody class="text-14">
                <template v-if="showGoogleReminders">
                  <template v-for="event in sortedEvents" :key="event.groupKey || event.id || event.requirementKey">
                    <!-- Main row -->
                    <tr>
                      <td
                        @click="handleShowReminderInfo(event)"
                        style="padding-left: 20px; color: var(--bs-primary); cursor: pointer"
                        class="align-text-top">
                        <strong>{{ event.name }}</strong>
                      </td>
                      <!-- Employers cell with expand/collapse (Action Center mode) -->
                      <td v-if="isActionCenterMode" class="align-text-top">
                        <button 
                          type="button" 
                          class="btn btn-link p-0 text-decoration-none text-primary text-14 d-flex justify-content-between w-100 gap-1 text-nowrap"
                          @click.stop="toggleRowExpand(event.groupKey || event.requirementKey)"
                        >
                          <strong>{{ event.employersCount }} Employer{{ event.employersCount !== 1 ? 's' : '' }} Selected</strong>
                          <i class="bi" :class="isRowExpanded(event.groupKey || event.requirementKey) ? 'bi-caret-up-fill' : 'bi-caret-down-fill'"></i>
                        </button>
                        <!-- Expanded employers list (inline, below the button) -->
                        <div v-if="isRowExpanded(event.groupKey || event.requirementKey)" class="mt-2 employers-inline-list">
                          <div 
                            v-for="emp in event.employers" 
                            :key="emp.employerId" 
                            class="employer-inline-item mb-1"
                          >
                            <span class="text-16">{{ emp.employerName }}</span>
                          </div>
                        </div>
                      </td>
                      <td class="align-text-top">
                        {{ formatDateGoogleReminders(event.due_date) }}
                      </td>
                      <td style="width: 220px" class="align-text-top">
                        <span v-if="todayDate == event.start_date">
                          Today
                        </span>
                        <span v-else>
                          {{ formatDateGoogleReminders(event?.start_date) }}
                          (
                          {{ daysDifferenceGoogleReminder(event.due_date, event?.start_date) }}
                          bus days before)
                        </span>
                      </td>
                      <td class="align-text-top">
                        {{ formatDateGoogleReminders(String(event.created_at || '').slice(0, 10)) }}
                      </td>
                    </tr>
                  </template>
                </template>
              </tbody>
            </table>
          </div>

          <!-- Detail view when clicking on an item -->
          <template v-else-if="showGoogleReminderInformation.show">
            <div
              style="padding-top: 10px; padding-bottom: 15px"
              class="back-to-main d-flex"
              @click="showGoogleReminderInformation.show = false">
              <i class="bi bi-chevron-left"></i>
              Back to Due Date Reminders Sent
            </div>

            <div class="preview-modal__reminder-date fill-preview">
              <div class="preview-modal__reminder-date-title">
                <b class="">Reminder For:</b>
              </div>
              <div class="response-preview">
                <span>{{ showGoogleReminderInformation.event.name }}</span>
              </div>
            </div>
            
            <div class="preview-modal__employer fill-preview">
              <b class="preview-modal__reminder-date-title">Due Date:</b>
              <div>{{ formatDateGoogleReminders(showGoogleReminderInformation.event.due_date) }}</div>
            </div>

            <div class="preview-modal__from fill-preview">
              <b class="preview-modal__reminder-date-title">Reminder Scheduled For:</b>
              <div>
                {{ formatDateGoogleReminders(showGoogleReminderInformation.event?.start_date) }} (
                {{
                  daysDifferenceGoogleReminder(
                    showGoogleReminderInformation.event.due_date,
                    showGoogleReminderInformation.event?.start_date
                  )
                }}
                business days before)
              </div>
            </div>

            <div class="preview-modal__from fill-preview">
              <b class="preview-modal__reminder-date-title">Reminder Sent:</b>
              <div>
                {{
                  formatDateGoogleReminders(
                    showGoogleReminderInformation?.event?.created_at?.slice(0, 10)
                  )
                }}
              </div>
            </div>

            <!-- Employer field: different display for Action Center vs legacy mode -->
            <div class="preview-modal__from fill-preview">
              <b class="preview-modal__reminder-date-title">Employer{{ isActionCenterMode && showGoogleReminderInformation.event.employers?.length > 1 ? 's' : '' }}:</b>
              <div v-if="isActionCenterMode && showGoogleReminderInformation.event.employers">
                <div v-for="emp in showGoogleReminderInformation.event.employers" :key="emp.employerId" class="mb-1">
                  {{ emp.employerName }}
                  <span v-if="!emp.success" class="text-danger text-12 ms-1">
                    <i class="bi bi-exclamation-triangle-fill"></i> Failed
                  </span>
                </div>
              </div>
              <div v-else>{{ actualEmployer.name }}</div>
            </div>

            <div class="preview-modal__recipients fill-preview">
              <div class="preview-modal__other-recipients" style="margin-top: 8px">
                <b class="preview-modal__reminder-date-title">Other Recipient(s):</b>
                <div class="other-emails-preview">
                  <!-- In Action Center mode, show emails per employer -->
                  <template v-if="isActionCenterMode && showGoogleReminderInformation.event.employers">
                    <div v-for="emp in showGoogleReminderInformation.event.employers" :key="emp.employerId" class="mb-1 text-14">
                      <strong>{{ emp.employerName }}:</strong> {{ emp.target_emails || 'No recipients' }}
                    </div>
                  </template>
                  <div v-else>
                    {{ showGoogleReminderInformation.event.target_emails }}
                  </div>
                </div>
              </div>
            </div>

            <div class="preview-modal__title fill-preview" style="border: none !important">
              <b class="preview-modal__reminder-date-title">From:</b>
              <div>
                <BcsDynamicText text="reminderEmailFrom"></BcsDynamicText>
              </div>
            </div>
          </template>
        </div>
        <div class="modal-footer">
          <button class="btn btn-primary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.bg-gray-200 {
  background-color: var(--bs-gray-200);
}

.modal-title {
  i {
    margin-right: 11px;
  }
}

.table-container-events {
  height: auto;
  max-height: 364px;
  overflow-y: auto;
}

.thead-custom-red th {
  background-color: transparent;
  border-radius: 4px;
  border-bottom: 1px solid var(--bs-gray-400);
}

.table-reminders-sent th {
  border-radius: 4px;
  border-bottom: 1px solid var(--bs-gray-400);
}

.table-reminders {
  margin-bottom: 0 !important;
}

.second-column {
  width: 250px !important;
}

.third-column {
  width: 170px !important;
}

.employers-column {
  width: 180px !important;
}


/* Inline employers list (inside the cell) */
.employers-inline-list {
  max-height: 150px;
  overflow-y: auto;
}

.employer-inline-item {
  display: flex;
  align-items: center;
  color: var(--bs-body-color);
}

.back-to-main {
  color: var(--bs-primary);
  font-weight: 700;
  cursor: pointer;
  font-size: 14px;

  i {
    padding-right: 5px;
  }
}

.preview-modal {
  &__title,
  &__reminder-date,
  &__message,
  &__internal-recipients,
  &__other-recipients,
  &__employer,
  &__from {
    display: flex;
    font-size: 14px;
  }
}

.preview-modal__reminder-date-title {
  min-width: 240px;
}

.fill-preview {
  border-bottom: 1px solid var(--bs-gray-400);
  padding-top: 12px;
  padding-bottom: 12px;
}
.sort-icon {
  padding-left: 5px;
  color: var(--bs-primary);
  cursor: pointer;
  background-color: var(--bs-info);
  border-radius: 4px;
}
.action-center-footer {
  display: flex;
  justify-content: flex-end;
  padding-bottom: 20px;
  padding-right: 16px;
}
</style>